import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import classNames from 'classnames'
import {
  type ActionItem,
  FullPageWrapper,
  Icon,
  SpinnerLoader,
  Tooltip,
  Typography,
  CalendarDropdown,
  Button,
} from 'shared/ui'
import { useElementWidth } from 'shared/hooks'
import { CONTACTS_METRIC_COLORS } from 'entities/Analytics/lib/constants'
import { BreakdownFilter } from 'pages/analytics/ui/BreakdownFilter/BreakdownFilter'
import { AnalyticsHeader, TrendLabel, AnalyticsEmptyState } from 'pages/analytics/ui'

import {
  ContactsAnalyticsPageProvider,
  useContactsAnalyticsPageContext,
} from 'pages/analytics/pages/contacts/context/contactsAnalyticsPageContext'
import { ChartFilter } from 'pages/analytics/ui/ChartFilter'
import { ContactsComparisonTable } from 'pages/analytics/pages/contacts/ui/ComparisonTable'
import styles from './styles.module.scss'
import { ContactsBarChart, ContactsLineChart } from './Chart'

export const ContactsAnalyticsPageContent = observer(() => {
  const {
    calendarFilterStore,
    breakdownFilterStore,
    openExportModal,
    pageLoading,
    chartDataStore,
    chartFilterStore,
    chartLoading,
    comparisonTableStore,
  } = useContactsAnalyticsPageContext()
  const { clearAllFilters, hasAppliedFilters } = breakdownFilterStore
  const { getStatisticByKey, hasStatistic } = chartDataStore
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set())
  const filtersRowRef = useRef<HTMLDivElement | null>(null)
  const filtersRowWidth = useElementWidth(filtersRowRef)

  const exportActions: ActionItem[] = [
    {
      text: 'Export data',
      icon: 'download',
      onAction: openExportModal,
    },
  ]

  const handleToggleMetric = (metricKey: string) => {
    setHiddenMetrics((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(metricKey)) {
        newSet.delete(metricKey)
      } else {
        newSet.add(metricKey)
      }
      return newSet
    })
  }

  const contactsStat = getStatisticByKey('new_contacts')
  const optedOutStat = getStatisticByKey('opted_out')

  const renderContent = () => {
    if (pageLoading) {
      return <SpinnerLoader ariaLabel={'ContactsAnalyticsPage'} />
    }

    if (!hasStatistic) {
      return <AnalyticsEmptyState />
    }

    return (
      <>
        <div className={styles.kpiSection}>
          <div
            className={classNames(styles.kpiCard, styles.kpiNewContacts)}
            onClick={() => handleToggleMetric('new_contacts')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('new_contacts')
                  ? 'var(--gray-10)'
                  : CONTACTS_METRIC_COLORS.new_contacts,
              }}
            />
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiItemHeader}>
                <Typography variant={'body-rg-regular'} color={'tertiary'} ariaLabel={'TotalTitle'}>
                  New contacts
                </Typography>
                <Tooltip
                  placement='top'
                  breakWord
                  label='The total number of contacts that were created during the selected date range.'
                >
                  <Icon icon='question' fontSize={14} color={'var(--content-neutral-primary)'} />
                </Tooltip>
              </div>
              <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'TotalValue'}>
                {contactsStat?.value?.toLocaleString() || '0'}
              </Typography>
              {contactsStat?.trend && (
                <TrendLabel trend={contactsStat.trend} displayMode='percent' />
              )}
            </div>
          </div>
          <div
            className={classNames(styles.kpiCard, styles.kpiOptedOut)}
            onClick={() => handleToggleMetric('opted_out')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('opted_out')
                  ? 'var(--gray-10)'
                  : CONTACTS_METRIC_COLORS.opted_out,
              }}
            />
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiItemHeader}>
                <Typography variant={'body-rg-regular'} color={'tertiary'} ariaLabel={'OpenTitle'}>
                  Opted-out
                </Typography>
                <Tooltip
                  placement='top'
                  breakWord
                  label='The total number of contacts that opted-out of receiving text messages during the selected date range.'
                >
                  <Icon icon='question' fontSize={14} color={'var(--content-neutral-primary)'} />
                </Tooltip>
              </div>
              <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'OpenValue'}>
                {optedOutStat?.value?.toLocaleString() || '0'}
              </Typography>
              {optedOutStat?.trend && (
                <TrendLabel trend={optedOutStat.trend} displayMode='percent' />
              )}
            </div>
          </div>
        </div>

        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <ChartFilter store={chartFilterStore} />
          </div>

          <div className={styles.chartArea}>
            {chartLoading ? (
              <SpinnerLoader ariaLabel={'ContactsAnalyticsPage'} />
            ) : (
              <>
                {chartFilterStore.chartType === 'line' ? (
                  <ContactsLineChart
                    chartDataStore={chartDataStore}
                    hiddenMetrics={hiddenMetrics}
                  />
                ) : (
                  <ContactsBarChart chartDataStore={chartDataStore} hiddenMetrics={hiddenMetrics} />
                )}
              </>
            )}
          </div>
        </div>

        {hasAppliedFilters && (
          <div className={styles.tableSection}>
            <ContactsComparisonTable store={comparisonTableStore} />
          </div>
        )}
      </>
    )
  }

  return (
    <FullPageWrapper className={styles.page}>
      <AnalyticsHeader title='Contacts' actions={exportActions} />

      <div className={styles.filtersRow} ref={filtersRowRef}>
        <CalendarDropdown store={calendarFilterStore} />
        <BreakdownFilter store={breakdownFilterStore} parentWidth={filtersRowWidth} />
        {hasAppliedFilters && (
          <Button
            contained='secondary'
            color={'var(--content-primary-secondary)'}
            typeBtn='text'
            text='Clear all'
            onClick={clearAllFilters}
          />
        )}
      </div>

      {renderContent()}
    </FullPageWrapper>
  )
})

export const ContactsAnalyticsPage = () => (
  <ContactsAnalyticsPageProvider>
    <ContactsAnalyticsPageContent />
  </ContactsAnalyticsPageProvider>
)
